import { Card } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { SidebarProvider } from '@/components/ui/sidebar'
import { ApiKeysSection } from './components/ApiKeysSection'
import { EventHubSection } from './components/EventHubSection'
import { AssetsManager } from './components/AssetsManager'
import { DriversManager } from './components/DriversManager'
import { EquipmentManager } from './components/EquipmentManager'
import { AlertRulesManager } from './components/AlertRulesManager'
import { ReportStudio } from './components/ReportStudio'
import { User, Key, Cloud, Bell, Shield, Truck, Users, Package, FileText } from 'lucide-react'

export function Settings() {
  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <AppSidebar />

        <main className="flex-1 overflow-auto bg-background">
          <div className="container mx-auto py-8 px-6 max-w-6xl">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold">Settings & Management</h1>
              <p className="mt-2 text-muted-foreground">
                Manage your fleet assets, drivers, integrations, and preferences
              </p>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="assets" className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 md:grid-cols-9 lg:w-auto">
                <TabsTrigger value="assets" className="gap-2">
                  <Truck className="h-4 w-4" />
                  <span className="hidden sm:inline">Assets</span>
                </TabsTrigger>
                <TabsTrigger value="drivers" className="gap-2">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Drivers</span>
                </TabsTrigger>
                <TabsTrigger value="equipment" className="gap-2">
                  <Package className="h-4 w-4" />
                  <span className="hidden sm:inline">Equipment</span>
                </TabsTrigger>
                <TabsTrigger value="profile" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">Profile</span>
                </TabsTrigger>
                <TabsTrigger value="api-keys" className="gap-2">
                  <Key className="h-4 w-4" />
                  <span className="hidden sm:inline">API Keys</span>
                </TabsTrigger>
                <TabsTrigger value="eventhub" className="gap-2">
                  <Cloud className="h-4 w-4" />
                  <span className="hidden sm:inline">Event Hub</span>
                </TabsTrigger>
                <TabsTrigger value="notifications" className="gap-2">
                  <Bell className="h-4 w-4" />
                  <span className="hidden sm:inline">Notifications</span>
                </TabsTrigger>
                <TabsTrigger value="reports" className="gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Reports</span>
                </TabsTrigger>
                <TabsTrigger value="security" className="gap-2">
                  <Shield className="h-4 w-4" />
                  <span className="hidden sm:inline">Security</span>
                </TabsTrigger>
              </TabsList>

              {/* Assets Tab */}
              <TabsContent value="assets" className="space-y-6">
                <AssetsManager />
              </TabsContent>

              {/* Drivers Tab */}
              <TabsContent value="drivers" className="space-y-6">
                <DriversManager />
              </TabsContent>

              {/* Equipment Tab */}
              <TabsContent value="equipment" className="space-y-6">
                <EquipmentManager />
              </TabsContent>

              {/* Profile Tab */}
              <TabsContent value="profile" className="space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Profile Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Name</label>
                      <input
                        type="text"
                        className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                        defaultValue="Demo User"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email</label>
                      <input
                        type="email"
                        className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                        defaultValue="demo@fleetvision.app"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Company Name</label>
                      <input
                        type="text"
                        className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                        defaultValue="FleetVision Demo"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <input
                        type="tel"
                        className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex gap-3">
                    <button className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                      Save Changes
                    </button>
                    <button className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent">
                      Cancel
                    </button>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Subscription</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Current Plan</span>
                      <span className="text-sm font-medium">Enterprise</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Billing Cycle</span>
                      <span className="text-sm font-medium">Monthly</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Next Billing Date</span>
                      <span className="text-sm font-medium">December 12, 2025</span>
                    </div>
                  </div>
                  <button className="mt-6 w-full rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent">
                    Manage Subscription
                  </button>
                </Card>
              </TabsContent>

              {/* API Keys Tab */}
              <TabsContent value="api-keys">
                <ApiKeysSection />
              </TabsContent>

              {/* Event Hub Tab */}
              <TabsContent value="eventhub">
                <EventHubSection />
              </TabsContent>

              {/* Notifications Tab */}
              <TabsContent value="notifications" className="space-y-6">
                <AlertRulesManager />
              </TabsContent>

              {/* Reports Tab */}
              <TabsContent value="reports" className="space-y-6">
                <ReportStudio />
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security" className="space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Password</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Update your password regularly to keep your account secure
                  </p>
                  <button className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent">
                    Change Password
                  </button>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Two-Factor Authentication</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <button className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                    Enable 2FA
                  </button>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Active Sessions</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">Current Session</p>
                        <p className="text-xs text-muted-foreground">Chrome on macOS • Last active now</p>
                      </div>
                      <span className="text-xs text-green-500">Active</span>
                    </div>
                  </div>
                  <button className="mt-6 w-full rounded-md border border-destructive text-destructive px-4 py-2 text-sm font-medium hover:bg-destructive hover:text-destructive-foreground">
                    Sign Out All Other Sessions
                  </button>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </SidebarProvider>
  )
}
